import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Input,
  Modal,
  Select,
  Typography,
  notification,
  Checkbox,
} from 'antd';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import { BankResponse } from '../../model/Bank.model';
import { CardTypeResponse } from '../../model/Card-type.model';
import { CreditCardRequest } from '../../model/Credit-card.model';
import BankService from '../../service/bank-service/Bank.service';
import CardTypeService from '../../service/card-type-service/Card-type.service';
import CreditCardService from '../../service/credit-card-service/Credit-card.service';
import CustomerService from '../../service/customer-service/Customer.service';

interface CreateCreditCardModalProps {
  customerId: number;
  onSuccess: () => void;
  trigger?: React.ReactNode;
}

const { Text } = Typography;

const CreateCreditCardModal: React.FC<CreateCreditCardModalProps> = ({
  customerId,
  onSuccess,
  trigger,
}) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<CreditCardRequest>({
    customerId: customerId,
    cardNumber: '',
    cardTypeId: 0,
    bankId: 0,
    expiryDate: '',
    notes: '',
    creditLimit: 0,
    cyclePaymentDate: undefined,
    status: '',
    defaultFeeRate: 0,
  });
  const [creditLimitDisplay, setCreditLimitDisplay] = useState('');
  const [defaultFeeRateDisplay, setDefaultFeeRateDisplay] = useState('0');
  const [isSameAsCustomer, setIsSameAsCustomer] = useState(true);
  const [customerName, setCustomerName] = useState('');

  const [bankList, setBankList] = useState<BankResponse[]>([]);
  const [cardTypeList, setCardTypeList] = useState<CardTypeResponse[]>([]);

  useEffect(() => {
    const fetchBankList = async () => {
      const data = await BankService.getBankList();
      setBankList(data);
    };
    fetchBankList();
  }, []);

  useEffect(() => {
    const fetchCardTypes = async () => {
      const data = await CardTypeService.getCardTypeList();
      setCardTypeList(data);
    };
    fetchCardTypes();
  }, []);

  useEffect(() => {
    // Fetch customer information to get the name
    const fetchCustomerName = async () => {
      try {
        // Use customer service to get customer information
        const customerDetails =
          await CustomerService.getCustomerById(customerId);

        if (customerDetails && customerDetails.name) {
          setCustomerName(customerDetails.name);
          if (isSameAsCustomer) {
            setFormData((prev) => ({
              ...prev,
              cardHolderName: customerDetails.name,
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching customer details:', error);
      }
    };

    if (open) {
      fetchCustomerName();
    }
  }, [customerId, open, isSameAsCustomer]);

  useEffect(() => {
    // Update card holder name when checkbox changes
    if (isSameAsCustomer) {
      setFormData((prev) => ({
        ...prev,
        cardHolderName: customerName,
      }));
    }
  }, [isSameAsCustomer, customerName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.bankId || !formData.cardTypeId) {
      notification.error({
        message: 'Vui lòng chọn ngân hàng và loại thẻ',
      });
      return;
    }
    try {
      await CreditCardService.createCreditCard(customerId, formData);
      onSuccess();
      notification.success({ message: 'Tạo thẻ thành công!' });
      setOpen(false);
      setFormData({
        customerId: customerId,
        cardNumber: '',
        cardTypeId: 0,
        bankId: 0,
        expiryDate: '',
        notes: '',
        creditLimit: 0,
        cyclePaymentDate: undefined,
        status: '',
        defaultFeeRate: 0,
        cardHolderName: '',
      });
    } catch (error: any) {
      console.error('Lỗi khi tạo thẻ mới:', error);
      if (error.response && error.response.status === 409) {
        notification.error({
          message: 'Lỗi khi tạo thẻ mới',
          description: error.response.data,
        });
      } else {
        notification.error({
          message: 'Lỗi khi tạo thẻ mới',
          description: 'Vui lòng thử lại.',
        });
      }
    }
  };

  // Hàm format số tiền sang dạng có dấu chấm
  const formatCurrency = (value: string) => {
    const number = value.replace(/\D/g, '');
    return number.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  // Hàm xử lý khi người dùng nhập hạn mức
  const handleCreditLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formattedValue = formatCurrency(value);
    setCreditLimitDisplay(formattedValue);
    setFormData((prev) => ({
      ...prev,
      creditLimit: Number(value.replace(/\./g, '')),
    }));
  };

  // Add a new handler for the default fee rate input
  const handleDefaultFeeRateChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    // Allow only numbers and one decimal point
    if (
      /^(\d+)?([.])?([0-9]{0,2})?$/.test(value) &&
      parseFloat(value || '0') <= 100
    ) {
      setDefaultFeeRateDisplay(value);
      setFormData((prev) => ({
        ...prev,
        defaultFeeRate: value === '' ? 0 : parseFloat(value),
      }));
    }
  };

  return (
    <>
      {trigger ? (
        React.cloneElement(trigger as React.ReactElement, {
          onClick: () => setOpen(true),
        })
      ) : (
        <Button
          type="default"
          icon={<PlusOutlined />}
          onClick={() => setOpen(true)}
          color="blue"
          variant="filled"
        >
          <strong>Thêm thẻ mới</strong>
        </Button>
      )}

      <Modal
        title="Tạo thẻ mới"
        visible={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={700}
        centered
        className="responsive-modal"
      >
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="mb-2">
              <Text strong className="block mb-1">
                Số thẻ <span className="text-red-500">*</span>
              </Text>
              <Input
                name="cardNumber"
                placeholder="Nhập số thẻ"
                value={formData.cardNumber}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    cardNumber: e.target.value,
                  }))
                }
                required
                className="w-full"
              />
            </div>

            <div className="mb-2">
              <Text strong className="block mb-1">
                Tên chủ thẻ
              </Text>
              <div className="space-y-2">
                <Input
                  name="cardHolderName"
                  value={formData.cardHolderName || ''}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      cardHolderName: e.target.value,
                    }))
                  }
                  disabled={isSameAsCustomer}
                  placeholder={
                    isSameAsCustomer
                      ? 'Sử dụng tên khách hàng'
                      : 'Nhập tên chủ thẻ'
                  }
                />
                <Checkbox
                  checked={isSameAsCustomer}
                  onChange={(e) => setIsSameAsCustomer(e.target.checked)}
                >
                  Khách hàng {customerName ? `(${customerName})` : ''}
                </Checkbox>
              </div>
            </div>

            <div className="mb-2">
              <Text strong className="block mb-1">
                Ngày hết hạn
              </Text>
              <Input
                type="date"
                name="expiryDate"
                value={formData.expiryDate}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    expiryDate: e.target.value,
                  }))
                }
                className="w-full"
              />
              {formData.expiryDate && (
                <Text type="secondary" className="text-xs mt-1 block">
                  {format(new Date(formData.expiryDate), 'dd/MM/yyyy')}
                </Text>
              )}
            </div>

            <div className="mb-2">
              <Text strong className="block mb-1">
                Ngân hàng <span className="text-red-500">*</span>
              </Text>
              <Select
                showSearch
                value={
                  formData.bankId !== 0 ? formData.bankId.toString() : undefined
                }
                onChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    bankId: Number(value),
                  }));
                }}
                style={{ width: '100%' }}
                placeholder="Ngân hàng"
                filterOption={(input, option) =>
                  typeof option?.children === 'string' &&
                  (option.children as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                className="w-full"
              >
                {bankList.map((bank) => (
                  <Select.Option key={bank.id} value={bank.id.toString()}>
                    {bank.name}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div>
              <Text strong>
                Loại thẻ <span className="text-red-500">*</span>
              </Text>
              <Select
                showSearch
                value={
                  formData.cardTypeId !== 0
                    ? formData.cardTypeId.toString()
                    : undefined
                }
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    cardTypeId: Number(value),
                  }))
                }
                style={{ width: '100%' }}
                placeholder="Loại thẻ"
                filterOption={(input, option) =>
                  typeof option?.children === 'string' &&
                  (option.children as string)
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {cardTypeList.length ? (
                  cardTypeList.map((type) => (
                    <Select.Option
                      key={type.cardTypeId}
                      value={type.cardTypeId.toString()}
                    >
                      {type.name}
                    </Select.Option>
                  ))
                ) : (
                  <Select.Option value="0" disabled>
                    Chưa có loại thẻ nào
                  </Select.Option>
                )}
              </Select>
            </div>

            <div>
              <Text strong>Hạn mức thẻ</Text>
              <Input
                name="creditLimit"
                placeholder="Nhập hạn mức"
                value={creditLimitDisplay}
                onChange={handleCreditLimitChange}
              />
            </div>

            <div>
              <Text strong>Trạng thái</Text>
              <Select
                style={{ width: '100%' }}
                placeholder="Chọn trạng thái"
                value={formData.status || undefined}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    status: value,
                  }))
                }
              >
                <Select.Option value="LUU_THE">Lưu thẻ</Select.Option>
                <Select.Option value="TRA_THE">Trả thẻ</Select.Option>
              </Select>
            </div>

            <div>
              <Text strong>Phí mặc định (%)</Text>
              <Input
                name="defaultFeeRate"
                placeholder="Nhập phí mặc định (0-100%)"
                value={defaultFeeRateDisplay}
                onChange={handleDefaultFeeRateChange}
                addonAfter="%"
                step="0.01"
                min="0"
                max="100"
              />
              <Text type="secondary" className="text-xs">
                Phí mặc định (0-100%)
              </Text>
            </div>

            <div>
              <Text strong>Ngày đến hạn hàng tháng</Text>
              <Input
                name="cyclePaymentDate"
                placeholder="Nhập ngày chu kỳ (1-30)"
                value={formData.cyclePaymentDate?.toString() || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  if (!value) {
                    setFormData((prev) => ({
                      ...prev,
                      cyclePaymentDate: undefined,
                    }));
                  } else {
                    const numericValue = Number(value);
                    if (numericValue >= 1 && numericValue <= 30) {
                      setFormData((prev) => ({
                        ...prev,
                        cyclePaymentDate: numericValue,
                      }));
                    }
                  }
                }}
              />
              <Text type="secondary" className="text-xs">
                Ngày thanh toán trong tháng (có thể để trống)
              </Text>
            </div>
          </div>

          <div className="mt-4">
            <Text strong>Ghi chú</Text>
            <Input.TextArea
              name="notes"
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              placeholder="Thêm ghi chú"
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <Button type="default" onClick={() => setOpen(false)}>
              Hủy
            </Button>
            <Button type="primary" htmlType="submit">
              Tạo thẻ
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
};

export default CreateCreditCardModal;
